import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, notifyError } from "@/lib/api";
import { toast } from "sonner";
import { UsersThree, Plus, Trash, ShieldCheck, PaperPlaneTilt, Crown, Eye, ArrowClockwise, ClockCounterClockwise, Warning, FunnelSimple, DownloadSimple } from "@phosphor-icons/react";

const ROLE_OPTIONS = [
  { id: "admin", label: "Admin", hint: "Everything except billing & deleting the company" },
  { id: "member", label: "Member", hint: "Day-to-day: receipts, bank, invoices, compliance" },
  { id: "accountant", label: "Accountant", hint: "Read-only — free seat" },
];

const roleBadge = (role) => {
  const map = {
    owner: { c: "#FFB020", i: Crown, label: "Owner" },
    admin: { c: "#7Cc4ff", i: ShieldCheck, label: "Admin" },
    member: { c: "#00A86B", i: UsersThree, label: "Member" },
    accountant: { c: "#AEB8B4", i: Eye, label: "Accountant" },
  };
  return map[role] || map.member;
};

export default function Team() {
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [seats, setSeats] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);
  const [fActor, setFActor] = useState("all");
  const [fType, setFType] = useState("all");
  const [fRange, setFRange] = useState("all");

  const load = useCallback(async () => {
    try { const r = await api.get("/team"); setData(r.data); } catch (e) { notifyError(e); }
    try { const a = await api.get("/activity"); setActivity(a.data.items || []); } catch { /* optional */ }
    try { const s = await api.get("/billing/seats"); setSeats(s.data); } catch { /* optional */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Seat-limit guard: a new Admin/Member becomes a paid seat once the included
  // allowance is used up. Accountants are always free.
  const willAddPaidSeat = seats && (role === "admin" || role === "member") && seats.used >= (seats.included || 0);
  const perSeat = seats?.monthly_per_seat ?? 12;

  const rangeSince = () => {
    const now = new Date();
    if (fRange === "week") { const d = new Date(now); d.setDate(d.getDate() - 7); return d.toISOString(); }
    if (fRange === "month") { const d = new Date(now); d.setMonth(d.getMonth() - 1); return d.toISOString(); }
    if (fRange === "30") { const d = new Date(now); d.setDate(d.getDate() - 30); return d.toISOString(); }
    return "";
  };

  const exportActivity = async () => {
    try {
      const params = {};
      if (fActor !== "all") params.actor = fActor;
      if (fType !== "all") params.type = fType;
      const since = rangeSince();
      if (since) params.since = since;
      const r = await api.get("/activity/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `percen360-activity-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { notifyError(e); }
  };

  const resendInvite = async (invite_id) => {
    try {
      const r = await api.post(`/team/invite/${invite_id}/resend`);
      if (r.data.emailed) toast.success(`Reminder emailed to ${r.data.email}`);
      else { toast.message(`Couldn't email ${r.data.email}`, { description: "Copy the invite link to share it manually." });
        if (r.data.link) navigator.clipboard?.writeText(r.data.link).catch(() => {}); }
    } catch (e) { notifyError(e); }
  };

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (willAddPaidSeat && !window.confirm(`Heads up: ${email.trim()} will be a paid seat (+${perSeat === 12 ? "€12" : "€" + perSeat}/mo, or €9/mo on annual) added to your next renewal. Continue?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/team/invite", { email, role });
      if (r.data.emailed) toast.success(`Invite emailed to ${r.data.email}`);
      else toast.message(`Invite created for ${r.data.email}`, {
        description: r.data.email_error === "domain not verified"
          ? "Email not sent (verify your domain). Copy the link below to share it manually."
          : "Email not sent — copy the link below to share it manually.",
        duration: 8000,
      });
      if (!r.data.emailed && r.data.link) navigator.clipboard?.writeText(r.data.link).catch(() => {});
      setEmail(""); setRole("member"); load();
    } catch (e) { notifyError(e); } finally { setBusy(false); }
  };

  const changeRole = async (member_user_id, newRole) => {
    try { const r = await api.post("/team/role", { member_user_id, role: newRole }); setData(r.data); }
    catch (e) { notifyError(e); }
  };

  const removeMember = async (member_user_id, label) => {
    if (!window.confirm(`Remove ${label} from the team? They'll lose access immediately.`)) return;
    try { const r = await api.delete(`/team/member/${member_user_id}`); setData(r.data); }
    catch (e) { notifyError(e); }
  };

  const cancelInvite = async (invite_id) => {
    try { const r = await api.delete(`/team/invite/${invite_id}`); setData(r.data); }
    catch (e) { notifyError(e); }
  };

  const canManage = data?.can_manage;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Team</div>
          <h1 className="font-display text-4xl">Your company &amp; people</h1>
          <p className="text-white/60 text-sm mt-2">
            Invite teammates to share {data?.company_name ? <strong className="text-white/80">{data.company_name}</strong> : "your company"}&apos;s data.
            Owners &amp; admins can edit; accountants are read-only (and free).
          </p>
        </div>
        {data && (
          <div className="glass rounded-xl px-4 py-3 text-right">
            <div className="text-[11px] uppercase tracking-wider text-white/45">Team</div>
            <div className="font-display text-2xl" data-testid="team-count">{data.member_count} {data.member_count === 1 ? "person" : "people"}</div>
            <div className="text-[11px] text-white/40 mt-0.5">{data.billable_seats} paid seat{data.billable_seats === 1 ? "" : "s"} · accountants free</div>
          </div>
        )}
      </div>

      {canManage && (
        <form onSubmit={invite} className="glass rounded-xl p-6 grid grid-cols-1 md:grid-cols-[1fr,180px,auto] gap-4 items-end" data-testid="invite-form">
          <div>
            <label>Invite by email</label>
            <input type="email" data-testid="invite-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" required />
          </div>
          <div>
            <label>Role</label>
            <select data-testid="invite-role" value={role} onChange={(e) => setRole(e.target.value)}>
              {ROLE_OPTIONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
          <button type="submit" disabled={busy} data-testid="invite-submit" className="btn-gold justify-center">
            <PaperPlaneTilt size={16} weight="fill" /> {busy ? "Sending…" : "Send invite"}
          </button>
          <div className="md:col-span-3 text-[11px] text-white/40 -mt-1">{ROLE_OPTIONS.find((r) => r.id === role)?.hint}</div>
          {willAddPaidSeat && (
            <div data-testid="seat-guard-warning" className="md:col-span-3 -mt-1 flex items-start gap-2 rounded-lg border border-[#FFB020]/30 bg-[#FFB020]/10 px-3 py-2 text-xs text-[#FFB020]">
              <Warning size={15} weight="fill" className="mt-0.5 shrink-0" />
              <span>You&apos;ve used all {seats.included} included seats — this teammate adds a <strong>paid seat</strong> (€{perSeat}/mo, or €{seats.annual_per_seat_monthly}/mo on annual) at your next renewal.</span>
            </div>
          )}
        </form>
      )}

      {/* Pending invites */}
      {data?.invites?.length > 0 && (
        <div className="glass rounded-xl overflow-hidden">
          <div className="px-5 py-3 eyebrow flex items-center gap-2"><PaperPlaneTilt size={14} weight="fill" style={{ color: "#FFB020" }} /> Pending invites</div>
          <table className="data">
            <thead><tr><th>Email</th><th>Role</th><th></th></tr></thead>
            <tbody>
              {data.invites.map((iv) => (
                <tr key={iv.invite_id} data-testid={`invite-row-${iv.invite_id}`}>
                  <td>{iv.email}</td>
                  <td className="text-white/60">{iv.role_label}</td>
                  <td className="text-right">
                    {canManage && <>
                      <button data-testid={`resend-invite-${iv.invite_id}`} onClick={() => resendInvite(iv.invite_id)} title="Resend invite" className="text-white/40 hover:text-[#FFB020] mr-3"><ArrowClockwise size={16} /></button>
                      <button data-testid={`cancel-invite-${iv.invite_id}`} onClick={() => cancelInvite(iv.invite_id)} title="Cancel invite" className="text-white/40 hover:text-[#E63946]"><Trash size={16} /></button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Members */}
      <div className="glass rounded-xl overflow-hidden">
        <div className="px-5 py-3 eyebrow flex items-center gap-2"><UsersThree size={14} weight="fill" /> Members</div>
        {!data ? (
          <div className="p-12 text-center text-white/40 text-sm">Loading…</div>
        ) : (
          <table className="data">
            <thead><tr><th>Person</th><th>Role</th><th></th></tr></thead>
            <tbody>
              {data.members.map((m) => {
                const b = roleBadge(m.role);
                return (
                  <tr key={m.user_id} data-testid={`member-row-${m.user_id}`}>
                    <td>
                      <div className="font-medium">{m.name || m.email} {m.is_you && <span className="text-white/40 text-xs">(you)</span>}</div>
                      <div className="text-white/45 text-xs">{m.email}</div>
                    </td>
                    <td>
                      {canManage && m.role !== "owner" ? (
                        <select data-testid={`role-select-${m.user_id}`} value={m.role} onChange={(e) => changeRole(m.user_id, e.target.value)}
                          className="text-sm rounded-lg bg-black/30 border border-white/10 px-2 py-1" style={{ width: "auto" }}>
                          {ROLE_OPTIONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                        </select>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-sm" style={{ color: b.c }}><b.i size={15} weight="fill" /> {b.label}</span>
                      )}
                    </td>
                    <td className="text-right">
                      {canManage && m.role !== "owner" && !m.is_you && (
                        <button data-testid={`remove-member-${m.user_id}`} onClick={() => removeMember(m.user_id, m.name || m.email)} className="text-white/40 hover:text-[#E63946]"><Trash size={16} /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="glass rounded-xl overflow-hidden" data-testid="activity-feed">
        <div className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="eyebrow flex items-center gap-2"><ClockCounterClockwise size={14} weight="fill" /> Recent activity</div>
          {activity.length > 0 && (
            <div className="flex items-center gap-2">
              <FunnelSimple size={14} className="text-white/40" />
              <select data-testid="activity-filter-person" value={fActor} onChange={(e) => setFActor(e.target.value)}
                className="text-xs rounded-lg bg-black/30 border border-white/10 px-2 py-1" style={{ width: "auto" }}>
                <option value="all">Everyone</option>
                {[...new Set(activity.map((a) => a.actor_name || a.actor_email).filter(Boolean))].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <select data-testid="activity-filter-type" value={fType} onChange={(e) => setFType(e.target.value)}
                className="text-xs rounded-lg bg-black/30 border border-white/10 px-2 py-1" style={{ width: "auto" }}>
                <option value="all">All types</option>
                {[...new Set(activity.map((a) => a.resource_type).filter(Boolean))].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select data-testid="activity-filter-range" value={fRange} onChange={(e) => setFRange(e.target.value)}
                className="text-xs rounded-lg bg-black/30 border border-white/10 px-2 py-1" style={{ width: "auto" }}>
                <option value="all">All time</option>
                <option value="week">This week</option>
                <option value="month">This month</option>
                <option value="30">Last 30 days</option>
              </select>
              <button data-testid="activity-export-btn" onClick={exportActivity} title="Download filtered activity as CSV"
                className="btn-ghost text-xs py-1"><DownloadSimple size={14} weight="fill" /> CSV</button>
            </div>
          )}
        </div>
        {(() => {
          const now = new Date();
          const cutoff = (() => {
            if (fRange === "week") { const d = new Date(now); d.setDate(d.getDate() - 7); return d; }
            if (fRange === "month") { const d = new Date(now); d.setMonth(d.getMonth() - 1); return d; }
            if (fRange === "30") { const d = new Date(now); d.setDate(d.getDate() - 30); return d; }
            return null;
          })();
          const filtered = activity.filter((a) =>
            (fActor === "all" || (a.actor_name || a.actor_email) === fActor) &&
            (fType === "all" || a.resource_type === fType) &&
            (!cutoff || (a.at && new Date(a.at) >= cutoff)));
          if (activity.length === 0) return <div className="p-8 text-center text-white/40 text-sm">No activity yet. Receipts, invoices and filings your team adds will appear here.</div>;
          if (filtered.length === 0) return <div className="p-8 text-center text-white/40 text-sm">No activity matches these filters.</div>;
          return (
            <ul className="divide-y divide-white/5">
              {filtered.map((a) => (
                <li key={a.activity_id} data-testid={`activity-${a.activity_id}`} className="px-5 py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm truncate">{a.summary || `${a.action} ${a.resource_type}`}</div>
                    <div className="text-[11px] text-white/45">{a.actor_name || a.actor_email}</div>
                  </div>
                  <div className="text-[11px] text-white/35 whitespace-nowrap">{(a.at || "").slice(0, 16).replace("T", " ")}</div>
                </li>
              ))}
            </ul>
          );
        })()}
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-white/40">
        Seat billing (charging for extra Admin/Member seats) is now active — see the Billing page for seat usage. Accountants are always free.
      </motion.div>
    </div>
  );
}
